"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LuX } from "react-icons/lu";
import { cn, isToastElement } from "../../lib/utils";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { DIALOG_STYLES } from "./constants";
import {
  DialogOverlayProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogFooterProps,
  DialogTitleProps,
  DialogDescriptionProps,
} from "./types";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(DIALOG_STYLES.overlay, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      bottomSheet = false,
      noDescription,
      disableAnimation = false,
      overlayClassName,
      onPointerDownOutside,
      ...props
    },
    ref
  ) => {
    const isMobile = useMediaQuery("(max-width: 640px)");
    const showBottomSheet = bottomSheet && isMobile && !disableAnimation;

    const handlePointerDownOutside: NonNullable<DialogContentProps["onPointerDownOutside"]> = (event) => {
      if (isToastElement(event.target)) {
        event.preventDefault();
        return;
      }
      onPointerDownOutside?.(event);
    };

    const contentBaseStyles = disableAnimation
      ? DIALOG_STYLES.contentStatic
      : DIALOG_STYLES.content;

    const contentLayoutStyles = disableAnimation
      ? DIALOG_STYLES.contentCenteredStatic
      : showBottomSheet
        ? DIALOG_STYLES.contentBottomSheet
        : DIALOG_STYLES.contentCentered;

    return (
      <DialogPortal>
        {disableAnimation ? (
          <DialogPrimitive.Overlay
            className={cn(DIALOG_STYLES.overlayStatic, overlayClassName)}
          />
        ) : (
          <DialogOverlay className={overlayClassName} />
        )}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(contentBaseStyles, contentLayoutStyles, className)}
          aria-describedby={
            noDescription ? undefined : props["aria-describedby"]
          }
          onPointerDownOutside={handlePointerDownOutside}
          {...props}
        >
          {children}
          <DialogPrimitive.Close className={DIALOG_STYLES.closeButton}>
            <LuX className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div className={cn(DIALOG_STYLES.header, className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
  <div className={cn(DIALOG_STYLES.footer, className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(DIALOG_STYLES.title, className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(DIALOG_STYLES.description, className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
